import React, { useState } from "react";
import {
  ServerCrash,
  WifiOff,
  FileQuestion,
  MapPinOff,
  ArrowLeft,
  RotateCcw,
  BookOpen,
  Activity,
  LayoutDashboard,
} from "lucide-react";

// 404 - Page Not Found
export const PageNotFound = ({ onGoDashboard, onDocumentation }) => {
  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row min-h-[600px] border border-slate-100 mt-10">
      {/* Left Content */}
      <div className="flex-1 p-8 sm:p-10 lg:p-16 xl:p-20 flex flex-col justify-center relative z-10 bg-white">
        <div className="mb-8 lg:hidden">
          <MapPinOff
            className="w-16 h-16 text-indigo-500"
            strokeWidth={1.5}
          />
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 text-indigo-600 font-semibold text-sm w-max mb-6">
          <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
          Error 404
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-slate-900 tracking-tight mb-6">
          Looks like you've wandered off the path.
        </h1>

        <p className="text-lg lg:text-xl text-slate-500 mb-10 max-w-xl leading-relaxed">
          The page you are looking for might have been removed, had its name
          changed, or is temporarily unavailable. Let's get you back to
          familiar territory.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={onGoDashboard}
            className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-indigo-200 hover:-translate-y-0.5"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <button
            type="button"
            onClick={onDocumentation}
            className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl transition-all duration-200"
          >
            <BookOpen className="w-5 h-5 text-slate-400" />
            Read Documentation
          </button>
        </div>
      </div>

      {/* Right Visual - Desktop */}
      <div className="hidden lg:flex flex-1 bg-slate-50 relative items-center justify-center overflow-hidden border-l border-slate-100">
        <div className="absolute top-1/4 -left-12 w-96 h-96 bg-indigo-200/50 rounded-full mix-blend-multiply blur-3xl opacity-70 animate-pulse" />

        <div className="absolute bottom-1/4 right-12 w-80 h-80 bg-purple-200/50 rounded-full mix-blend-multiply blur-3xl opacity-70" />

        <div className="relative z-10 flex items-center justify-center">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-3xl rounded-full scale-150 shadow-2xl border border-white/50" />

          <MapPinOff
            className="w-64 h-64 xl:w-80 xl:h-80 text-indigo-400 relative z-20 drop-shadow-xl"
            strokeWidth={1}
          />

          <div
            className="absolute -top-12 -right-12 bg-white p-4 rounded-2xl shadow-xl rotate-12 animate-bounce"
            style={{ animationDuration: "3s" }}
          >
            <FileQuestion
              className="w-10 h-10 text-purple-400"
              strokeWidth={1.5}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// 503 - Server Not Reachable
export const ServerNotReachable = ({ onRetry, onCheckStatus, retrying }) => {
  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row min-h-[600px] border border-slate-100 mt-10">
      {/* Left Content */}
      <div className="flex-1 p-8 sm:p-10 lg:p-16 xl:p-20 flex flex-col justify-center relative z-10 bg-white">
        <div className="mb-8 lg:hidden">
          <ServerCrash
            className="w-16 h-16 text-rose-500"
            strokeWidth={1.5}
          />
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 text-rose-600 font-semibold text-sm w-max mb-6">
          <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
          Error 503
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-slate-900 tracking-tight mb-6">
          Connection Failed.
        </h1>

        <p className="text-lg lg:text-xl text-slate-500 mb-10 max-w-xl leading-relaxed">
          We are currently unable to connect to the server. This might be due
          to temporary network issues, high traffic, or routine maintenance on
          our end.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-rose-200 hover:-translate-y-0.5"
          >
            <RotateCcw
              className={`w-5 h-5 ${retrying ? "animate-spin" : ""}`}
            />
            {retrying ? "Checking..." : "Try Again"}
          </button>

          <button
            type="button"
            onClick={onCheckStatus}
            className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl transition-all duration-200"
          >
            <Activity className="w-5 h-5 text-slate-400" />
            Check System Status
          </button>
        </div>
      </div>

      {/* Right Visual - Desktop */}
      <div className="hidden lg:flex flex-1 bg-slate-900 relative items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundSize: "20px 20px",
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1px)",
          }}
        />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-rose-500/20 rounded-full blur-[100px]" />

        <div className="relative z-10 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-800/50 backdrop-blur-xl rounded-[3rem] scale-125 shadow-2xl border border-slate-700" />

          <div className="relative z-20 flex flex-col items-center">
            <ServerCrash
              className="w-52 h-52 xl:w-64 xl:h-64 text-rose-400 drop-shadow-[0_0_30px_rgba(251,113,133,0.3)]"
              strokeWidth={1}
            />

            <div className="mt-8 flex gap-4">
              <div className="w-3 h-3 rounded-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,1)] animate-pulse" />
              <div className="w-3 h-3 rounded-full bg-slate-600" />
              <div className="w-3 h-3 rounded-full bg-slate-600" />
            </div>
          </div>

          <div
            className="absolute -bottom-10 -left-12 xl:-left-16 bg-slate-800 p-5 rounded-2xl shadow-2xl border border-slate-700 -rotate-6 animate-bounce"
            style={{ animationDuration: "4s" }}
          >
            <WifiOff
              className="w-10 h-10 xl:w-12 xl:h-12 text-slate-400"
              strokeWidth={1.5}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App
export default function App() {
  const [activeView, setActiveView] = useState("404");
  const [retrying, setRetrying] = useState(false);

  const handleGoDashboard = () => {
    // Change "/" to your actual dashboard route if required.
    window.location.href = "/";
  };

  const handleDocumentation = () => {
    // Replace with your documentation URL.
    window.open("/docs", "_blank", "noopener,noreferrer");
  };

  const handleRetry = async () => {
    setRetrying(true);

    try {
      // Replace "/" with your actual backend health-check endpoint.
      const response = await fetch("/");

      if (response.ok) {
        window.location.reload();
      } else {
        window.alert("The server is still unavailable. Please try again.");
      }
    } catch (error) {
      window.alert("Unable to connect to the server. Please try again later.");
    } finally {
      setRetrying(false);
    }
  };

  const handleCheckStatus = () => {
    // Replace with your actual system status page URL.
    window.open("/status", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-slate-100 font-sans flex flex-col">
      {/* Navigation Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold text-lg sm:text-xl">
            <LayoutDashboard className="w-6 h-6 text-indigo-600 shrink-0" />
            <span>Web App System</span>
          </div>

          <div className="flex bg-slate-100 p-1 rounded-xl shrink-0">
            <button
              type="button"
              onClick={() => setActiveView("404")}
              className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeView === "404"
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
              }`}
            >
              404 Page
            </button>

            <button
              type="button"
              onClick={() => setActiveView("503")}
              className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeView === "503"
                  ? "bg-white text-rose-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
              }`}
            >
              503 Page
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center p-4 sm:p-8 lg:p-12">
        {activeView === "404" ? (
          <PageNotFound
            onGoDashboard={handleGoDashboard}
            onDocumentation={handleDocumentation}
          />
        ) : (
          <ServerNotReachable
            onRetry={handleRetry}
            onCheckStatus={handleCheckStatus}
            retrying={retrying}
          />
        )}
      </main>
    </div>
  );
}